import { useState } from "react";
import { doc, updateDoc } from "firebase/firestore";
import { auth, db } from "../firebase";
import CheckoutBrick from "./CheckoutBrick";
import { fetchCheckoutPlano } from "../utils/checkoutPlano";
import { semearContaNova } from "../utils/semearContaNova";
import PlanCards from "./PlanCards";
import { CompanyBrand, LogoutButton } from "./AppHeader";
import { usePrecos } from "../hooks/usePrecos";
import { useAssinaturaPlay } from "../hooks/useAssinaturaPlay";
import "./planos.css";

const GERAR_URL = "https://us-central1-matriz3dsite.cloudfunctions.net/gerarPreferencia";

const VALIDAR_URL = "https://us-central1-matriz3dsite.cloudfunctions.net/validarCupom";

export default function EscolherPlano({ uid, perfil, onUpdate, onLogout }) {
  const [assinando,    setAssinando]    = useState(null);
  const [erro,         setErro]         = useState("");
  const [preferenceId, setPreferenceId] = useState(null);
  const [checkoutId, setCheckoutId] = useState(null);
  const [planoBrick,   setPlanoBrick]   = useState(null);
  const [brickAmount,  setBrickAmount]  = useState(null);
  const [couponInput,  setCouponInput]  = useState("");
  const [cupomOk,      setCupomOk]      = useState(null);
  const [erroCupom,    setErroCupom]    = useState("");
  const [validandoCupom, setValidandoCupom] = useState(false);
  const [mostrarCupom, setMostrarCupom] = useState(false);

  const precos = usePrecos();
  // App Android: a assinatura é pela Google Play (a loja exige); no site e no PC, Mercado Pago
  const play = useAssinaturaPlay({ uid, onUpdate, setErro });
  const jaUsouTrial = !!perfil?.trialUsed;

  const escolherTrial = async () => {
    setAssinando("trial");
    try {
      const trialEndsAt = new Date(Date.now() + 30 * 24 * 60 * 60 * 1000);
      await updateDoc(doc(db, "users", uid), { plan: "trial", trialEndsAt, trialUsed: true });
      await semearContaNova(uid);
      onUpdate(prev => ({ ...prev, plan: "trial", trialEndsAt, trialUsed: true }));
    } catch (e) {
      console.error("Erro ao iniciar trial:", e);
      setErro("Erro ao iniciar trial. Tente novamente.");
    }
    setAssinando(null);
  };

  const aplicarCupom = async (plano) => {
    const code = couponInput.trim();
    if (!code) return;
    setValidandoCupom(true); setErroCupom(""); setCupomOk(null);
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetch(VALIDAR_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ code, plano: plano || "monthly" }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || "Cupom inválido");
      setCupomOk(d);
    } catch(e) { setErroCupom(e.message); }
    setValidandoCupom(false);
  };

  const assinar = async (plano) => {
    if (play.usarPlay) { await play.assinar(); return; }
    setAssinando(plano); setErro("");
    try {
      const token = await auth.currentUser?.getIdToken(true);
      const res = await fetchCheckoutPlano(GERAR_URL, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body:    JSON.stringify({ plano, couponCode: cupomOk?.code || couponInput.trim() || null, origin: window.location.origin }),
      });
      if (!res.ok) {
        const d = await res.json().catch(() => ({}));
        throw new Error(d.error || "Erro ao iniciar checkout");
      }
      const data = await res.json();
      setPlanoBrick(plano);
      setPreferenceId(data.preferenceId); setCheckoutId(data.checkoutId);
      setBrickAmount(data.amount || null);
    } catch(e) {
      setErro(e.message || "Erro ao abrir checkout. Tente novamente.");
    }
    setAssinando(null);
  };

  const handleSuccess = (plano) => {
    const expiresAt = new Date(Date.now() + (plano === "annual" ? 365 : 30) * 86400000);
    onUpdate(prev => ({ ...prev, plan: plano, planExpiresAt: expiresAt }));
    setPreferenceId(null);
  };

  return (
    <>
      <div className="planos-tela">
        <main className="planos-conteudo" aria-labelledby="planos-titulo">
          <header className="planos-topo">
            <CompanyBrand compact empresa={perfil?.empresa} logo={perfil?.logoUrl} subtitle="Último passo para começar" />
            <LogoutButton onClick={onLogout} />
          </header>

          <div className="planos-hero">
            <span className="planos-eyebrow">SUA CONTA ESTÁ PRONTA</span>
            <h1 id="planos-titulo">{jaUsouTrial ? "Assine para começar" : "Escolha como começar"}</h1>
            <p>{jaUsouTrial
              ? "Assine o Print Control e organize pedidos, custos e vendas das suas impressões 3D."
              : "Teste tudo grátis por 30 dias, sem cartão, ou assine e use sem limites."}</p>
          </div>

          <PlanCards
            precos={precos}
            precoTexto={play.usarPlay ? play.precoPlay : null}
            jaUsouTrial={jaUsouTrial}
            assinando={assinando || (play.ocupado === "assinar" ? "monthly" : null)}
            onTrial={escolherTrial}
            onAssinar={assinar}
          />

          {erro && <p className="planos-erro" role="alert">{erro}</p>}

          {play.usarPlay && (
            <div className="planos-cupom">
              <button type="button" className="planos-link" onClick={play.restaurar} disabled={!!play.ocupado}>
                {play.ocupado === "restaurar" ? "Conferindo…" : "Já assinei pela Google Play"}
              </button>
            </div>
          )}

          {/* Cupom de desconto (Mercado Pago): fica recolhido até a pessoa pedir */}
          {!play.usarPlay && <section className="planos-cupom" aria-label="Cupom de desconto">
            {!mostrarCupom && !cupomOk ? (
              <button type="button" className="planos-link" onClick={() => setMostrarCupom(true)}>Tenho um cupom de desconto</button>
            ) : (
              <form className="planos-cupom-form" onSubmit={e => { e.preventDefault(); aplicarCupom(planoBrick || "monthly"); }}>
                <label htmlFor="planos-cupom-codigo">Cupom de desconto</label>
                <div className="planos-cupom-campo">
                  <input id="planos-cupom-codigo" value={couponInput} autoComplete="off" placeholder="Digite o código" autoFocus={!cupomOk}
                    onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCupomOk(null); setErroCupom(""); }} />
                  <button type="submit" disabled={validandoCupom || !couponInput.trim()}>{validandoCupom ? "Conferindo…" : "Aplicar"}</button>
                </div>
                {cupomOk
                  ? <p className="planos-cupom-ok" role="status">✓ Cupom aplicado: desconto de R$ {cupomOk.discount?.toFixed(2)} · total R$ {cupomOk.finalAmount?.toFixed(2)}</p>
                  : <small>O desconto vale para a assinatura.</small>}
                {erroCupom && <p className="planos-cupom-erro" role="alert">{erroCupom}</p>}
              </form>
            )}
          </section>}

          <ul className="planos-garantias">
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M12 3 4.5 6v5.5c0 4.6 3.1 8.1 7.5 9.5 4.4-1.4 7.5-4.9 7.5-9.5V6L12 3Z M8.8 12.2l2.2 2.2 4.4-4.6" /></svg>
              Reembolso garantido em até 7 dias
            </li>
            <li>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M20 11.5a8 8 0 0 1-11.7 7.1L4 20l1.4-4.1A8 8 0 1 1 20 11.5Z" /></svg>
              Suporte via WhatsApp (73) 99907-3081
            </li>
          </ul>
        </main>
      </div>

      {preferenceId && (
        <CheckoutBrick
          preferenceId={preferenceId} checkoutId={checkoutId}
          plano={planoBrick}
          uid={uid}
          onSuccess={handleSuccess}
          onClose={() => { setPreferenceId(null); setPlanoBrick(null); setBrickAmount(null); }}
          amount={brickAmount}
          couponCode={cupomOk?.code || null}
        />
      )}
    </>
  );
}
